/*
 * ─── แก้รูป (ลบด้วย AI + แปรงทาสี + ประทับตรา): the numbers — no DOM, so the tests load this ──────────────────────────
 * Choices (24 ต.ค. 2569): ก AI fills in what is painted over (LaMa on the group's VM) plus hand tools · ก the result is a
 * new file in the picture, the original kept for "คืนต้นฉบับ" · ก every member, a daily limit · ก brush + rectangle.
 * Only a patch around what was painted goes to the VM (at most ERASE_SIDE a side): quicker, and every pixel outside the
 * painted part stays exactly as it was.
 */

export interface Rect { x: number; y: number; w: number; h: number }
export interface View { s: number; ox: number; oy: number }

/** the biggest patch side sent (worker/rules.ts ERASE_MAX_SIDE) */
export const ERASE_SIDE = 1024;
/** the editable picture's biggest side (the same as แต่งภาพ / ลบพื้นหลัง — src/editor/photo.ts EDIT_MAX_SIDE) */
export const FIX_MAX_SIDE = 4096;
/** the painted mask is kept at most this big a side (it only marks where; it is scaled to the patch) */
export const MASK_MAX_SIDE = 2048;
/** undo steps keep only the changed part; together at most this many bytes (oldest dropped first) */
export const UNDO_MAX_BYTES = 160 * 1024 * 1024;
/** brush size on the screen (css px) */
export const BRUSH_MIN = 6, BRUSH_MAX = 160, BRUSH_DEFAULT = 36;

/**
 * The patch sent to the VM around the painted box `b` (picture px): the box plus room on every side for the model to see
 * what is around (half the box's longer side, at least 48 px), kept inside the picture, then scaled down (`k`) so its
 * longer side is at most `maxSide`. Null when nothing is painted.
 */
export function eraseRegion(b: Rect | null, W: number, H: number, maxSide = ERASE_SIDE): (Rect & { k: number; pw: number; ph: number }) | null {
  if (!b || b.w <= 0 || b.h <= 0) return null;
  const m = Math.max(48, Math.ceil(Math.max(b.w, b.h) / 2));
  const x = Math.max(0, Math.floor(b.x - m)), y = Math.max(0, Math.floor(b.y - m));
  const r = Math.min(W, Math.ceil(b.x + b.w + m)), btm = Math.min(H, Math.ceil(b.y + b.h + m));
  const w = r - x, h = btm - y;
  if (w <= 0 || h <= 0) return null;
  const k = Math.min(1, maxSide / Math.max(w, h));
  return { x, y, w, h, k, pw: Math.max(1, Math.round(w * k)), ph: Math.max(1, Math.round(h * k)) };
}

/** the box of every set cell in a w × h on/off grid, or null */
export function boxOf(on: Uint8Array, w: number, h: number): Rect | null {
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) if (on[row + x]) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; y1 = y; }
  }
  return x1 < 0 ? null : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/** on/off from RGBA pixels: a pixel counts when its alpha is above `min` (the painted mask is drawn see-through elsewhere) */
export function maskCells(rgba: Uint8ClampedArray, min = 40): Uint8Array {
  const out = new Uint8Array(rgba.length / 4);
  for (let i = 0; i < out.length; i++) out[i] = rgba[i * 4 + 3] > min ? 1 : 0;
  return out;
}

/**
 * Grow the on cells by `r` (a square, two passes): the soft edges of letters next to what was painted are filled too —
 * otherwise a faint outline of the word stays.
 */
export function grow(on: Uint8Array, w: number, h: number, r: number): Uint8Array {
  if (r <= 0) return on.slice();
  const tmp = new Uint8Array(on.length), out = new Uint8Array(on.length);
  for (let y = 0; y < h; y++) {
    let last = -Infinity;
    const row = y * w;
    for (let x = 0; x < w; x++) { if (on[row + x]) last = x; if (x - last <= r) tmp[row + x] = 1; }
    last = Infinity;
    for (let x = w - 1; x >= 0; x--) { if (on[row + x]) last = x; if (last - x <= r) tmp[row + x] = 1; }
  }
  for (let x = 0; x < w; x++) {
    let last = -Infinity;
    for (let y = 0; y < h; y++) { if (tmp[y * w + x]) last = y; if (y - last <= r) out[y * w + x] = 1; }
    last = Infinity;
    for (let y = h - 1; y >= 0; y--) { if (tmp[y * w + x]) last = y; if (last - y <= r) out[y * w + x] = 1; }
  }
  return out;
}

/** the points to stamp from a to b, `step` apart (b itself included; a was stamped by the previous step) */
export function stampsBetween(a: { x: number; y: number }, b: { x: number; y: number }, step: number): Array<{ x: number; y: number }> {
  const d = Math.hypot(b.x - a.x, b.y - a.y), n = Math.max(1, Math.ceil(d / Math.max(0.5, step)));
  return Array.from({ length: n }, (_, i) => ({ x: a.x + (b.x - a.x) * (i + 1) / n, y: a.y + (b.y - a.y) * (i + 1) / n }));
}

/** a rectangle grown to hold another (null = empty) */
export function union(a: Rect | null, b: Rect): Rect {
  if (!a) return { ...b };
  const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y);
  return { x, y, w: Math.max(a.x + a.w, b.x + b.w) - x, h: Math.max(a.y + a.h, b.y + b.h) - y };
}

/** a rectangle made whole pixels and kept inside W × H (null when nothing is left) */
export function clampRect(r: Rect, W: number, H: number): Rect | null {
  const x = Math.max(0, Math.floor(r.x)), y = Math.max(0, Math.floor(r.y));
  const x1 = Math.min(W, Math.ceil(r.x + r.w)), y1 = Math.min(H, Math.ceil(r.y + r.h));
  return x1 > x && y1 > y ? { x, y, w: x1 - x, h: y1 - y } : null;
}

/** how many of the newest undo steps (sizes in bytes, oldest first) fit in `max`; the newest is always kept */
export function keepSteps(sizes: number[], max = UNDO_MAX_BYTES): number {
  let total = 0, n = 0;
  for (let i = sizes.length - 1; i >= 0; i--) {
    if (n > 0 && total + sizes[i] > max) break;
    total += sizes[i]; n++;
  }
  return n;
}

/** the picture fitted in the stage with a margin */
export function fitView(W: number, H: number, sw: number, sh: number, margin = 24): View {
  const s = Math.max(0.0001, Math.min((sw - 2 * margin) / W, (sh - 2 * margin) / H));
  return { s, ox: (sw - W * s) / 2, oy: (sh - H * s) / 2 };
}

/** zoom by `f` around the screen point (px, py), between `min` and 16× */
export function zoomAt(v: View, f: number, px: number, py: number, min: number): View {
  const s = Math.min(16, Math.max(min, v.s * f));
  const k = s / v.s;
  return { s, ox: px - (px - v.ox) * k, oy: py - (py - v.oy) * k };
}

/** a hex colour from RGB */
export const hexOf = (r: number, g: number, b: number) => '#' + [r, g, b].map(n => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')).join('');

/** why a picture cannot be opened in แก้รูป (null = fine) */
export function fixProblem(w: number, h: number): string | null {
  if (!(w > 0 && h > 0)) return 'รูปนี้ยังโหลดไม่เสร็จ (หรือโหลดไม่ได้) รอสักครู่แล้วลองใหม่';
  if (w * h > FIX_MAX_SIDE * FIX_MAX_SIDE) {
    return `รูปใหญ่ ${w}×${h} px เกินที่แก้รูปรับ (${FIX_MAX_SIDE}×${FIX_MAX_SIDE}) กด "แต่งภาพ" ที่รูปก่อน ระบบจะย่อให้อัตโนมัติ แล้วลองใหม่`;
  }
  return null;
}

/**
 * Put the AI's result into the picture: each pixel is the original and the result mixed by the mask's weight (0–255,
 * soft at its edge), on all four channels. An opaque picture stays opaque everywhere.
 * (The first version cut a hole with the soft mask, then drew the result over it: at the soft edge the two partial
 * coverages left the pixels part see-through — a ring that a JPG file turned grey: BUG-67.)
 */
export function blendPatch(orig: Uint8ClampedArray, res: Uint8ClampedArray, weight: Uint8ClampedArray): Uint8ClampedArray {
  const out = new Uint8ClampedArray(orig.length);
  for (let i = 0; i < orig.length; i += 4) {
    const a = weight[i + 3] / 255, b = 1 - a;
    out[i] = Math.round(orig[i] * b + res[i] * a);
    out[i + 1] = Math.round(orig[i + 1] * b + res[i + 1] * a);
    out[i + 2] = Math.round(orig[i + 2] * b + res[i + 2] * a);
    out[i + 3] = Math.round(orig[i + 3] * b + res[i + 3] * a);
  }
  return out;
}

/** whether any pixel is not fully opaque (a JPG cannot keep them: it would turn them dark) */
export function hasSeeThrough(rgba: Uint8ClampedArray): boolean {
  for (let i = 3; i < rgba.length; i += 4) if (rgba[i] < 255) return true;
  return false;
}
